import { apiRequest } from "./baseApi";
import { parseJsonOrThrow } from "./responseUtils";

/**
 * GMA-1585 — read-only consent status for a client's sensors (Pontosense +
 * Apple Watch / HealthKit). Backed by the generalized sensor_consent_audit
 * table (GMA-1577 / cortex-be #358). The combined-status endpoint returns the
 * latest consent action per sensor so admins + caregivers can see, at a glance,
 * whether the client has signed each consent form.
 *
 * GET /api/v1/clients/:clientId/sensor-consent-status
 *   -> { clientId, status: {
 *          pontosense:   { consented, action, recordedAt, consentSource },
 *          watch_health: { consented, action, recordedAt, consentSource },
 *      } }
 *
 * Returns the `status` map (or {} when absent) so callers index by sensorType.
 *
 * Both the Watch pane and the Pontosense pane render on the same client profile,
 * so two consent badges mount together and would otherwise fire two identical
 * GETs on initial load. We coalesce concurrent in-flight requests per clientId
 * into one shared promise; the entry is cleared once it settles, so this is pure
 * in-flight dedup (never a stale cache) — a later mount re-fetches fresh data.
 */
const inFlightByClient = new Map();

export const fetchSensorConsentStatus = async (clientId) => {
  const existing = inFlightByClient.get(clientId);
  if (existing) return existing;

  const promise = (async () => {
    const res = await apiRequest(`clients/${clientId}/sensor-consent-status`, {
      version: "v1",
    });
    const data = await parseJsonOrThrow(res, "Sensor consent status");
    return data?.status || {};
  })();

  inFlightByClient.set(clientId, promise);
  try {
    return await promise;
  } finally {
    inFlightByClient.delete(clientId);
  }
};
